import {
  Allergy,
  AllergyService,
  CardiacRiskFactorPayload,
  Emphasis,
  HemstopPayload,
  LeePayload,
  MedicalHo,
  MedicalHoItem,
  MedicalHoItemCategory,
  MedicalHoService,
  MedicalScore,
  MedicalScoreName,
  MedicalScoreService,
  SourceType,
  SurgicalEvent
} from '@/modules/surgicalEvent';
import {
  BasicScratchMhoSuggestion,
  ClearScratchMhoSuggestion,
  DrawerScratchMhoSuggestion,
  ScratchMhoSuggestionData,
  ScratchMhoSuggestionType,
  ScratchSuggestion
} from './scratchMhoSuggestion.model';
import { ScratchSuggestionHelper } from './scratchSuggestionHelper';
import { captureThrowOnDev, isNotEmpty } from '@/util';
import { SnomedService } from '@/modules/snomed/snomed.service';

export type ScratchMhoSuggestionCategory = MedicalHoItemCategory | 'ALLERGIES';

export type MhoSuggestionsPerCategory = Partial<
  Record<ScratchMhoSuggestionCategory, ScratchSuggestion[]>
>;

export type PersistSuggestionPayload = {
  suggestion: ScratchSuggestion;
  activeSe: SurgicalEvent;
  category?: ScratchMhoSuggestionCategory;
  emphasis?: Emphasis;
  biChoiceChoice?: 'left' | 'right';
  drawerData?: Parameters<DrawerScratchMhoSuggestion['toRequestBody']>[0];
};

export type MasterClearPayload = {
  activeSe: SurgicalEvent | undefined;
  medicalHo: MedicalHo | undefined;
  map: MhoSuggestionsPerCategory;
};

export class ScratchMhoSuggestionService {
  private static readonly INSTANCE = new ScratchMhoSuggestionService();

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() {}

  public static getInstance() {
    return this.INSTANCE;
  }

  findDrawerSuggestionByQcode(qcode: string) {
    const s = ScratchSuggestionHelper.DrawerSuggestionsByQcode[qcode];
    if (!s) {
      throw new Error(`Drawer suggestion not found: ${qcode}`);
    }
    return s;
  }

  public cacheSnomeds(suggestionsMap: MhoSuggestionsPerCategory) {
    const snomedCodes = Object.values(suggestionsMap)
      .flat()
      .filter((s) => !!(s as BasicScratchMhoSuggestion).snomedCode)
      .map((s) => (s as BasicScratchMhoSuggestion).snomedCode) as string[];

    SnomedService.getInstance().cacheSnomedItems(snomedCodes);
  }

  public masterClear({
    activeSe,
    medicalHo,
    map
  }: MasterClearPayload): Promise<void> {
    if (!activeSe) {
      const err = new Error('Active surgical event is undefined');
      captureThrowOnDev(err);
      return Promise.reject(err);
    }

    if (!medicalHo) {
      const err = new Error('Medical ho is undefined');
      captureThrowOnDev(err);
      return Promise.reject(err);
    }

    const patientId = activeSe.patient.id;
    const promises: Promise<any>[] = [];

    if (!AllergyService.getInstance(patientId).isCleared()) {
      promises.push(
        this.addAllergy({ code: AllergyService.SC_CLEARED_CODE }, activeSe)
      );
    }

    const suggestionCategories = Object.keys(map).filter(
      (c) => c !== 'ALLERGIES'
    ) as MedicalHoItemCategory[];
    const mhos: Partial<MedicalHoItem>[] = [];
    const scores: Partial<MedicalScore>[] = [];
    suggestionCategories
      .filter((cat) => !MedicalHoService.getInstance(patientId).isCleared(cat))
      .forEach((category) => {
        const filteredSuggestions = filterMhoCategorySuggestions(
          map,
          category,
          medicalHo,
          activeSe
        );
        if (!filteredSuggestions) {
          return;
        }
        const isClearSuggestion = (
          suggestion: ScratchSuggestion
        ): suggestion is ClearScratchMhoSuggestion => {
          if (category === MedicalHoItemCategory.OBSTETRIC) {
            return suggestion.id === 'clear_obs_nullipara';
          }
          return suggestion.suggestionType === 'clear';
        };

        const availableClearSuggestion =
          filteredSuggestions.find(isClearSuggestion);
        if (availableClearSuggestion) {
          mhos.push(
            ...availableClearSuggestion
              .toRequestBody(activeSe)
              .map((data) => this.toMhoPayload(data, category, 'clear'))
          );
          scores.push(
            ...this.getClearScorePayloadsIfApplicable(
              availableClearSuggestion,
              activeSe
            )
          );
        } else {
          mhos.push({
            category,
            code: MedicalHoService.SC_CLEARED_CODE
          });
        }
      });

    promises.push(MedicalHoService.getInstance(patientId).addItems(mhos));
    if (isNotEmpty(scores)) {
      promises.push(
        MedicalScoreService.getInstance(patientId).finalizeMultiple(scores)
      );
    }

    return Promise.all(promises).then();
  }

  public persistSuggestion({
    suggestion,
    activeSe,
    biChoiceChoice,
    category,
    emphasis,
    drawerData
  }: PersistSuggestionPayload): Promise<void> {
    if (!activeSe) {
      const err = new Error('Active surgical event is undefined');
      captureThrowOnDev(err);
      return Promise.reject(err);
    }

    if (category === 'ALLERGIES') {
      return this.addAllergy(suggestion, activeSe);
    }

    const suggestionType = suggestion.suggestionType;

    let payload: ScratchMhoSuggestionData[];
    if (suggestionType === 'bi-choice') {
      if (!biChoiceChoice) {
        throw new Error('choice param should be provided');
      }
      payload = suggestion.toRequestBody(biChoiceChoice);
    } else if (suggestionType === 'clear') {
      payload = suggestion.toRequestBody(activeSe);
      if (!payload.length) {
        payload = [{ category, code: MedicalHoService.SC_CLEARED_CODE }];
      }
    } else if (suggestionType === 'drawer') {
      if (!drawerData) {
        throw new Error('drawerData param should be provided');
      }
      payload = suggestion.toRequestBody(drawerData);
    } else {
      payload = suggestion.toRequestBody();
    }

    const { allergies, mhos } = payload.reduce(
      (acc, data) => {
        if (data.category === 'ALLERGIES') {
          acc.allergies.push(data);
        } else {
          acc.mhos.push(data);
        }
        return acc;
      },
      { allergies: [], mhos: [] } as Record<
        'allergies' | 'mhos',
        ScratchMhoSuggestionData[]
      >
    );

    const mhoPayload: Partial<MedicalHoItem>[] = mhos.map((data) =>
      this.toMhoPayload(data, category, suggestionType)
    );

    if (emphasis) {
      mhoPayload.forEach((p) => (p.emphasis = emphasis));
    }

    const mhoProm = MedicalHoService.getInstance(activeSe.patient.id).addItems(
      mhoPayload
    );
    const allergyProm = allergies.map((al) => this.addAllergy(al, activeSe));
    const scoresToClear = this.getClearScorePayloadsIfApplicable(
      suggestion,
      activeSe
    );

    const clearScoresPromise = isNotEmpty(scoresToClear)
      ? MedicalScoreService.getInstance(activeSe.patient.id).finalizeMultiple(
          scoresToClear
        )
      : Promise.resolve();

    return Promise.all([...allergyProm, mhoProm, clearScoresPromise]).then();
  }

  private getClearScorePayloadsIfApplicable(
    suggestion: ScratchSuggestion,
    activeSe: SurgicalEvent
  ): Partial<MedicalScore>[] {
    if (suggestion.suggestionType === 'clear') {
      const scoresToClear = suggestion.getScoresToClear(activeSe);
      if (scoresToClear.length) {
        return scoresToClear.map((scoreName) => ({
          name: scoreName,
          payload: this.getClearScorePayload(scoreName)
        }));
      }
    }

    return [];
  }

  private getClearScorePayload(
    score: MedicalScoreName
  ): MedicalScore['payload'] {
    if (score === MedicalScoreName.LEE) {
      return {
        stroke: false,
        chronicKidneyFailure: false,
        heartFailure: false,
        diabetes: false,
        ischemicHeartDisease: false
      } as LeePayload;
    }

    if (score === MedicalScoreName.CARDIAC_RISK_FACTOR) {
      return {
        dyslipidemia: false,
        diabetes: false,
        highBloodPressure: false,
        smoker: false,
        vascularRiskFamily: false
      } as CardiacRiskFactorPayload;
    }

    if (score === MedicalScoreName.HEMSTOP) {
      return {
        persistentBleeding: false,
        frequentBruising: false,
        surgeryBleeding: false,
        dentalBleeding: false,
        postpartumBleeding: false,
        menorrhagia: false,
        familyBleedingDisorder: false
      } as HemstopPayload;
    }

    captureThrowOnDev(new Error(`Cannot clear score: ${score}`));
    return {};
  }

  private toMhoPayload(
    data: ScratchMhoSuggestionData,
    category: ScratchMhoSuggestionCategory | undefined,
    suggestionType: ScratchMhoSuggestionType
  ): Partial<MedicalHoItem> {
    if (data.category === 'ALLERGIES' || category === 'ALLERGIES') {
      throw new Error('Allergy data detected in mho payload');
    }
    return {
      ...data,
      category: data.category ?? category,
      scratchConsultationCleared:
        suggestionType === 'clear' &&
        data.code !== MedicalHoService.SC_CLEARED_CODE
    };
  }

  private addAllergy(
    s: ScratchSuggestion | ScratchMhoSuggestionData,
    activeSe: SurgicalEvent
  ): Promise<void> {
    const arr =
      'suggestionType' in s
        ? s.suggestionType === 'clear'
          ? s.toRequestBody(activeSe)
          : s.toRequestBody()
        : [s];
    return Promise.all(
      arr.map(({ code, notes }) =>
        AllergyService.getInstance(activeSe.patient.id).upsert({
          substanceCode: code,
          sourceType: SourceType.PREDEFINED,
          notes
        })
      )
    ).then();
  }
}

export function filterCategorySuggestions(
  map: MhoSuggestionsPerCategory,
  category: 'ALLERGIES',
  payload: Allergy[] | undefined,
  activeSe?: SurgicalEvent
): ScratchSuggestion[] | undefined;
export function filterCategorySuggestions(
  map: MhoSuggestionsPerCategory,
  category: Exclude<ScratchMhoSuggestionCategory, 'ALLERGIES'>,
  payload: MedicalHo | undefined,
  activeSe?: SurgicalEvent
): ScratchSuggestion[] | undefined;
export function filterCategorySuggestions(
  map: MhoSuggestionsPerCategory,
  category: ScratchMhoSuggestionCategory,
  payload: MedicalHo | Allergy[] | undefined,
  activeSe?: SurgicalEvent
): ScratchSuggestion[] | undefined {
  if (category === 'ALLERGIES' && (!payload || Array.isArray(payload))) {
    return filterAllergySuggestions(map, payload, activeSe);
  }

  if (category !== 'ALLERGIES' && !Array.isArray(payload)) {
    return filterMhoCategorySuggestions(map, category, payload, activeSe);
  }

  throw new Error('Incompatible overload types');
}

function filterAllergySuggestions(
  map: MhoSuggestionsPerCategory,
  allergies: Allergy[] | undefined,
  activeSe?: SurgicalEvent
): ScratchSuggestion[] | undefined {
  if (activeSe && AllergyService.getInstance(activeSe.patient.id).isCleared()) {
    return undefined;
  }
  const existingSubstances = new Set(allergies?.map((al) => al.substanceCode));
  const suggestions = map['ALLERGIES']?.filter((s) => s.applies(activeSe));
  if (!existingSubstances.size) {
    return suggestions;
  }

  return suggestions?.filter((s) => {
    if (s.suggestionType === 'clear') {
      return s.sticky;
    }
    if (s.suggestionType === 'basic') {
      return !existingSubstances.has(s.code);
    }

    return true;
  });
}

function filterMhoCategorySuggestions(
  map: MhoSuggestionsPerCategory,
  category: MedicalHoItemCategory,
  medicalHo: MedicalHo | undefined,
  activeSe?: SurgicalEvent
): ScratchSuggestion[] | undefined {
  if (activeSe?.patient) {
    if (MedicalHoService.getInstance(activeSe.patient.id).isCleared(category)) {
      return undefined;
    }
  }
  const serializeMho = <T extends { qcode?: string; code?: string }>(mho: T) =>
    `${mho.qcode ?? ''}___${mho.code ?? ''}`;

  const existingMhoSerialized = new Set<string>();
  const existingMhoSnomed = new Set<string>();
  medicalHo?.[category]?.forEach((mho) => {
    existingMhoSerialized.add(
      serializeMho(mho.isScore ? { qcode: mho.qcode } : mho)
    );
    if (mho.snomedCode) {
      existingMhoSnomed.add(mho.snomedCode);
    }
  });

  const suggestions = map[category]?.filter((s) => s.applies(activeSe));
  const existingNegatives = new Set<string>();
  const existingPositives = new Set<string>();
  const existingUniques = new Set<string>();
  const isExistingMho = (s: ScratchSuggestion) => {
    if (s.suggestionType === 'bi-choice') {
      return (
        existingMhoSerialized.has(serializeMho(s.toRequestBody('left')[0])) ||
        existingMhoSerialized.has(serializeMho(s.toRequestBody('right')[0]))
      );
    }
    if ('score' in s) {
      return existingMhoSerialized.has(serializeMho({ qcode: s.qcode }));
    }

    return (
      'qcode' in s &&
      (s.code
        ? existingMhoSerialized.has(serializeMho(s))
        : Array.from(existingMhoSerialized.values()).some((ser) =>
            ser.startsWith(`${s.qcode}___`)
          ))
    );
  };

  suggestions?.forEach((s) => {
    if ('qcode' in s && s.qcode && isExistingMho(s)) {
      if (s.mutuallyExclusivePerQcode === 'positive') {
        existingPositives.add(s.qcode);
      } else if (s.mutuallyExclusivePerQcode === 'negative') {
        existingNegatives.add(s.qcode);
      } else if (s.mutuallyExclusivePerQcode === 'uniq') {
        existingUniques.add(s.qcode);
      }
    }
  });

  if (!existingMhoSerialized.size && !existingMhoSnomed.size) {
    (
      suggestions?.find(
        (s) => s.suggestionType === 'clear'
      ) as ClearScratchMhoSuggestion
    )?.clearRealizedQcodes();
    return suggestions;
  }

  updateRealizedQcodes(suggestions, medicalHo?.[category], activeSe);

  const filteredSuggestions = suggestions?.filter((s) => {
    if (s.suggestionType === 'clear') {
      return s.sticky;
    }
    if (s.suggestionType === 'basic' && s.snomedCode) {
      return !existingMhoSnomed.has(s.snomedCode);
    }
    if ('qcode' in s && s.qcode && s.mutuallyExclusivePerQcode) {
      if (
        s.mutuallyExclusivePerQcode === 'positive' &&
        existingNegatives.has(s.qcode)
      ) {
        return false;
      } else if (
        s.mutuallyExclusivePerQcode === 'negative' &&
        existingPositives.has(s.qcode)
      ) {
        return false;
      } else if (s.mutuallyExclusivePerQcode === 'uniq') {
        return !existingUniques.has(s.qcode);
      }
    }
    // dirty alert
    if (s.qcode === 'Smoker') {
      return !Array.from(existingMhoSerialized).find((ser) =>
        ser.startsWith('Smoker')
      );
    }
    return !isExistingMho(s);
  });

  if (
    filteredSuggestions?.length === 1 &&
    filteredSuggestions[0].suggestionType === 'clear'
  ) {
    return undefined;
  }

  return filteredSuggestions;
}

function updateRealizedQcodes(
  suggestions: ScratchSuggestion[] | undefined,
  items: MedicalHoItem[] | undefined,
  activeSe: SurgicalEvent | undefined
) {
  const existingQcodes = items
    ?.map((it) => [it, ...(it.mergedWith ?? [])])
    .flat()
    .map((it) => it.qcode as string)
    .filter(Boolean);
  const removedWhileMerging =
    activeSe &&
    MedicalHoService.getInstance(
      activeSe.patient.id
    ).getRemovedItemsWhenMerging();

  if (existingQcodes && removedWhileMerging?.length) {
    existingQcodes.push(...removedWhileMerging.map((e) => e.qcode));
  }
  suggestions
    ?.filter((s) => s.suggestionType === 'clear')
    .forEach((s) =>
      (s as ClearScratchMhoSuggestion).setRealizedQcodes(existingQcodes)
    );
}
